import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { omit } from "lodash-es";
import moment, { Moment } from "moment";
import { forwardRef, useMemo } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import {
  DamageCostEvaluationFormPermissions,
  DamageCostEvaluationHelper,
} from "@/common/helpers/entity/damageCostEvaluation";
import { FormikHelper } from "@/common/helpers/formik";
import { IdHelper } from "@/common/helpers/id";
import { MemoHelper } from "@/common/helpers/memo";
import { PriceHelper } from "@/common/helpers/price";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useTenantProfileV2 } from "@/common/hooks/entity/tenant/useTenantProfileV2";
import { useEntityInputFormInfo } from "@/common/hooks/entity/useEntityInputFormInfo";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useCurrentCurrencyV2 } from "@/common/hooks/useCurrentCurrencyV2";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { Mapper } from "@/common/mapping/mapper";
import { RepairSpecDataInputDtoLocal } from "@/common/mapping/mappers/repairSpec";
import { VehicleDamageDataInputDtoLocal } from "@/common/mapping/mappers/vehicleDamage";
import { cast } from "@/common/ts/conversions";
import { BaseFormikValues } from "@/common/ts/error";
import { ViewVariant } from "@/common/ts/layout";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  ContractDto,
  ContractStage,
  DamageCostEvaluationCreateDto,
  DamageCostEvaluationDto,
  DamageCostEvaluationItemCreateDto,
  DamageCostEvaluationItemDamageInputDto,
  DamageCostEvaluationItemDto,
  DamageCostEvaluationItemUpdateDto,
  DamageCostEvaluationStage,
  DamageCostEvaluationUpdateDto,
  EntityType,
  GeneralDiscountInputDto,
  GeneralStageUpdateDtoOfDamageCostEvaluationStage,
  GeneralTaxInputDto,
  RepairSpecDataDto,
  RepairSpecDataInputDto,
  VehicleDamageDto,
  VehicleReferenceDto,
} from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import AppSplitDropdownButton from "../../Button/AppSplitDropdownButton";
import { DevRenderCount } from "../../Dev/DevRenderCount";
import FoldableBlock from "../../Display/FoldableBlock";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FileUploader from "../../Files/FileUploader";
import FormActions from "../../Form/FormActions";
import FormikComputedField from "../../Form/Formik/FormikComputedField";
import AppTextArea from "../../Form/Input/AppTextArea";
import AppIcon from "../../Icons/AppIcon";
import MemoWrapper from "../../Memo/MemoWrapper";
import AppTypography from "../../Text/AppTypography";
import ContractAutocompleteOrCreate from "../Contract/ContractAutocompleteOrCreate";
import CustomerAutocompleteOrCreate from "../Customer/CustomerAutocompleteOrCreate";
import DamageDetectionAutocomplete from "../DamageDetection/DamageDetectionAutocomplete";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import InferEntityAffiliationFromVehicleFormikComputedField from "../EntityAffiliation/InferEntityAffiliationFromVehicleFormikComputedField";
import {
  GeneralBillToInfoInput,
  prepareGeneralBillToInfoInputDtoForApi,
} from "../General/BillTo/GeneralBillToInfoInput";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import CurrencyAutocomplete from "../ReferenceData/CurrencyAutocomplete";
import VehicleActualStateView from "../Vehicle/VehicleActualState/VehicleActualStateView";
import VehicleAutocompleteOrCreate from "../Vehicle/VehicleAutocompleteOrCreate";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../components/BaseEntityCreateUpdate";
import { DamageCostEvaluationItemsInput } from "./CreateUpdate/DamageCostEvaluationItemsInput";
import DamageCostEvaluationAutocomplete from "./DamageCostEvaluationAutocomplete";

const defaultDisplayProps = {
  submitActionType: "save" as "save" | "saveAndComplete",
};

const defaultFormSettings = {
  /** Soft restriction - damages suggested for selection (users can change). */
  vehicleDamageIdsSuggested: undefined as string[] | undefined,
  /** Hard restriction - definitive damages only (users cannot change). */
  vehicleDamageIdsRestricted: undefined as string[] | undefined,
};
export type DamageCostEvaluationFormSettings = Partial<typeof defaultFormSettings>;

export function createNewEmptyItem(): DamageCostEvaluationItemValues {
  return {
    tempId: IdHelper.newId(),
    damages: [{}],
  };
}

type DefaultValues = {
  vehicleId?: DamageCostEvaluationCreateDto["vehicleId"];
  contractId?: DamageCostEvaluationCreateDto["contractId"];
  damageDetectionId?: DamageCostEvaluationCreateDto["damageDetectionId"];
  vehicle?: DamageCostEvaluationDto["vehicle"];
  vehicleDamageIds?: string[] | null;
  currency?: DamageCostEvaluationCreateDto["currency"];
  tenantRequestsMeta?: DamageCostEvaluationCreateDto["tenantRequestsMeta"];
  externalNumber?: DamageCostEvaluationCreateDto["externalNumber"];
  inspectedAt?: DamageCostEvaluationCreateDto["inspectedAt"];
  notes?: DamageCostEvaluationCreateDto["notes"];
  attachments?: DamageCostEvaluationDto["attachments"];
  billTo?: DamageCostEvaluationDto["billTo"];
  sourceEntity?: DamageCostEvaluationDto;
};

export type DamageCostEvaluationValues = BaseFormikValues &
  Omit<DamageCostEvaluationCreateDto, "items"> &
  Omit<DamageCostEvaluationUpdateDto, "items"> & {
    vehicle?: DamageCostEvaluationDto["vehicle"];
    contractLocal?: ContractDto;
    isAttachmentsUploading: boolean;
    isAttachmentsHaveErrors: boolean;
    initialAttachments: DamageCostEvaluationDto["attachments"];
    items?: DamageCostEvaluationItemValues[];

    // computed
    tax?: GeneralTaxInputDto | null;
    discount?: GeneralDiscountInputDto | null;
    subTotal?: number | null;
    subTotalIncDiscount?: number | null;
    total?: number | null;
    selectedDamageIds?: string[];
  };

export type DamageCostEvaluationItemValues = Omit<DamageCostEvaluationItemCreateDto, "damages"> &
  Omit<DamageCostEvaluationItemUpdateDto, "damages"> & {
    tempId: string;
    damages?: Array<DamageCostEvaluationItemDamageValues>;
    repairSpec?: RepairSpecDataInputDtoLocal; // & RepairSpecDataDto
    initialAttachments?:
      | DamageCostEvaluationItemDto["attachments"]
      | DamageCostEvaluationItemCreateDto["attachments"];
    isAttachmentsHaveErrors?: boolean;
    isNameEditedManually?: boolean;
    isInitiallyAutoSelectedBestMatchingRepairSpec?: boolean;

    // computed
    tax?: GeneralTaxInputDto | null;
    discount?: GeneralDiscountInputDto | null;
    subTotal?: number | null;
    subTotalIncDiscount?: number | null;
    total?: number | null;
  };

export type DamageCostEvaluationItemDamageValues = Omit<
  DamageCostEvaluationItemDamageInputDto,
  "damage"
> & {
  damage?: DamageCostEvaluationItemDamageDamageValues;
  /** DTO selected via autocomplete.  */
  damageLocal?: VehicleDamageDto;
};

export type DamageCostEvaluationItemDamageDamageValues = VehicleDamageDataInputDtoLocal;

export type DamageCostEvaluationCreateUpdateHandle = FormWrapperHandle;

export interface DamageCostEvaluationCreateUpdateOwnProps extends BaseEntityCreateUpdateInheritableProps<
  DamageCostEvaluationDto,
  DefaultValues
> {
  damageCostEvaluationId?: string;
  displayProps?: Partial<typeof defaultDisplayProps>;
  defaultFormPerms?: Partial<DamageCostEvaluationFormPermissions>;
  formSettings?: Partial<DamageCostEvaluationFormSettings>;
}

export type DamageCostEvaluationCreateUpdateProps = DamageCostEvaluationCreateUpdateOwnProps;

export default forwardRef<
  DamageCostEvaluationCreateUpdateHandle,
  DamageCostEvaluationCreateUpdateProps
>(function DamageCostEvaluationCreateUpdate(
  {
    damageCostEvaluationId,
    defaultValues,
    displayProps = defaultDisplayProps,
    defaultFormPerms,
    formSettings = defaultFormSettings,
    onCreate,
    onUpdate,
    onSave,
    onCancel,
  }: DamageCostEvaluationCreateUpdateProps,
  ref,
) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  formSettings = {
    ...defaultFormSettings,
    ...formSettings,
  };

  const formInfo = useEntityInputFormInfo({ entityId: damageCostEvaluationId });

  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();
  const profile = useUserProfile();
  const tenantProfile = useTenantProfileV2();
  const currentCurrency = useCurrentCurrencyV2({ useDefault: true });
  const userAffiliation = useUserAffiliation();

  const damageCostEvaluationRequest = useApiRequest(
    apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      damageCostEvaluationId: damageCostEvaluationId!,
    },
    {
      deps: [damageCostEvaluationId],
      skip: !damageCostEvaluationId,
    },
  );
  const damageCostEvaluation = damageCostEvaluationRequest?.data;

  const defaultVehicleDamagesRequest = useApiRequest(
    apiClient.vehicleDamagesApi.apiV1VehiclesVehicleIdDamagesByIdsGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: defaultValues?.vehicleId || "",
      vehicleDamageIds: defaultValues?.vehicleDamageIds || undefined,
    },
    {
      deps: [defaultValues?.vehicleId, defaultValues?.vehicleDamageIds],
      skip: !defaultValues?.vehicleId && !defaultValues?.vehicleDamageIds,
    },
  );
  const defaultVehicleDamages = defaultVehicleDamagesRequest?.data;

  const vehicleRequest = useApiRequest(
    apiClient.vehiclesApi.apiV1VehiclesVehicleIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: defaultValues?.vehicleId || "",
    },
    {
      skip: !defaultValues?.vehicleId || !!defaultValues?.vehicle,
      deps: [defaultValues?.vehicleId],
    },
  );

  const vehicleReference: VehicleReferenceDto | undefined = useMemo(() => {
    return vehicleRequest?.data
      ? {
          id: vehicleRequest.data.id,
          tenantId: vehicleRequest.data.tenantId || undefined,
          globalNumber: vehicleRequest.data.globalNumber,
          localNumber: vehicleRequest.data.localNumber,
          spec: vehicleRequest.data.spec,
          identificationNumber: vehicleRequest.data.identificationNumber,
          plateNo: vehicleRequest.data.plateNo,
        }
      : undefined;
  }, [vehicleRequest?.data]);

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: damageCostEvaluation && {
      idValue: damageCostEvaluation.id!,
      newTitle: damageCostEvaluation.localNumber || "",
    },
  });

  const formPerms = useMemo(
    () =>
      DamageCostEvaluationHelper.getEntityFormPermissions({
        formInfo,
        entity: damageCostEvaluation,
        defaultValues: defaultFormPerms,
      }),
    [formInfo, damageCostEvaluation],
  );

  const initialValues = useMemo<DamageCostEvaluationValues>(
    () => ({
      originalId: damageCostEvaluation?.originalId || defaultValues?.sourceEntity?.id || undefined,
      vehicleId:
        damageCostEvaluation?.vehicle?.id ||
        defaultValues?.vehicleId ||
        defaultValues?.sourceEntity?.vehicle?.id ||
        undefined,
      contractId:
        damageCostEvaluation?.contract?.id ||
        defaultValues?.contractId ||
        defaultValues?.sourceEntity?.contract?.id ||
        undefined,
      customerId:
        damageCostEvaluation?.customer?.id ||
        defaultValues?.sourceEntity?.customer?.id ||
        undefined,
      damageDetectionId:
        damageCostEvaluation?.damageDetectionId ||
        defaultValues?.damageDetectionId ||
        defaultValues?.sourceEntity?.damageDetectionId ||
        undefined,
      initialVehicleDamageIds:
        damageCostEvaluation?.initialVehicleDamageIds ||
        defaultValues?.vehicleDamageIds ||
        defaultValues?.sourceEntity?.initialVehicleDamageIds ||
        undefined,
      departmentIds:
        ArrayHelper.undefinedIfEmpty(damageCostEvaluation?.departmentIds) ||
        ArrayHelper.undefinedIfEmpty(defaultValues?.sourceEntity?.departmentIds) ||
        ArrayHelper.undefinedIfEmpty(userAffiliation.departmentIds) ||
        undefined,
      locationIds:
        ArrayHelper.undefinedIfEmpty(damageCostEvaluation?.locationIds) ||
        ArrayHelper.undefinedIfEmpty(defaultValues?.sourceEntity?.locationIds) ||
        ArrayHelper.undefinedIfEmpty(userAffiliation.locationIds) ||
        undefined,
      vehicle:
        damageCostEvaluation?.vehicle ||
        defaultValues?.vehicle ||
        defaultValues?.sourceEntity?.vehicle ||
        vehicleReference ||
        undefined,
      externalNumber:
        damageCostEvaluation?.externalNumber ||
        defaultValues?.externalNumber ||
        defaultValues?.sourceEntity?.externalNumber ||
        undefined,
      source: damageCostEvaluation?.source || undefined,
      currency:
        damageCostEvaluation?.currency ||
        defaultValues?.currency ||
        defaultValues?.sourceEntity?.currency ||
        currentCurrency.currency ||
        undefined,
      inspectedAt:
        damageCostEvaluation?.inspectedAt ||
        defaultValues?.inspectedAt ||
        moment().format(DATETIME_FORMATS.DISPLAY_DATETIME),
      inspector: {
        isCurrentUser: true,
        ...(damageCostEvaluation?.inspector || {}),
        phoneNumber: damageCostEvaluation?.inspector?.phoneNumber,
      },
      isUseSupplierBranding:
        damageCostEvaluation?.isUseSupplierBranding ??
        defaultValues?.sourceEntity?.isUseSupplierBranding ??
        true,
      notes:
        damageCostEvaluation?.notes ||
        defaultValues?.notes ||
        defaultValues?.sourceEntity?.notes ||
        "",
      attachments:
        ArrayHelper.undefinedIfEmpty(damageCostEvaluation?.attachments) ||
        ArrayHelper.undefinedIfEmpty(defaultValues?.attachments) ||
        ArrayHelper.undefinedIfEmpty(defaultValues?.sourceEntity?.attachments) ||
        undefined,
      initialAttachments:
        ArrayHelper.undefinedIfEmpty(damageCostEvaluation?.attachments) ||
        ArrayHelper.undefinedIfEmpty(defaultValues?.attachments) ||
        ArrayHelper.undefinedIfEmpty(defaultValues?.sourceEntity?.attachments) ||
        undefined,
      isAttachmentsUploading: false,
      isAttachmentsHaveErrors: false,
      items: (damageCostEvaluation?.items || defaultValues?.sourceEntity?.items)?.map((x) =>
        cast<DamageCostEvaluationItemValues>({
          ...x,
          id: x.id,
          tempId: x.id || IdHelper.newId(),
          name: x.name || undefined,
          damages: x.damages?.map((y) =>
            cast<DamageCostEvaluationItemDamageValues>({
              ...y,
              damage: y.damage ? Mapper.vehicleDamage.mapToDataInputDto(y.damage) : undefined,
            }),
          ),
          repairSpec: x.repairSpec
            ? ({
                ...x.repairSpec,
                ...Mapper.repairSpec.mapToDataInputDto(x.repairSpec),
              } as RepairSpecDataDto & RepairSpecDataInputDto)
            : undefined,
          notes: x.notes || undefined,
          attachments: x.attachments && x.attachments.length > 0 ? x.attachments : undefined,
          initialAttachments: x.attachments && x.attachments.length > 0 ? x.attachments : undefined,
          isAttachmentsHaveErrors: false,
          tax: x.tax,
          subTotal: x.subTotal,
          subTotalIncDiscount: x.subTotalIncDiscount,
          total: x.total,
          isNameEditedManually: !!x.name,
        }),
      ) ||
        defaultValues?.vehicleDamageIds?.map((damageId) => {
          const vehicleDamage = defaultVehicleDamages?.find((x) => x.id === damageId);
          return cast<DamageCostEvaluationItemValues>({
            tempId: IdHelper.newId(),
            damages: [
              cast<DamageCostEvaluationItemDamageValues>({
                damage: vehicleDamage
                  ? Mapper.vehicleDamage.mapToDataInputDto(vehicleDamage)
                  : undefined,
              }),
            ],
            initialAttachments: vehicleDamage?.attachments,
            attachments: vehicleDamage?.attachments,
            notes: vehicleDamage?.notes,
          });
        }) || [createNewEmptyItem()],

      isSendToCustomer:
        damageCostEvaluation?.isSendToCustomer ||
        defaultValues?.sourceEntity?.isSendToCustomer ||
        undefined,
      tenantRequestsMeta:
        damageCostEvaluation?.tenantRequestsMeta || defaultValues?.tenantRequestsMeta || undefined,
      tags: damageCostEvaluation?.tags || defaultValues?.sourceEntity?.tags || undefined,
      billTo:
        damageCostEvaluation?.billTo ||
        defaultValues?.billTo ||
        defaultValues?.sourceEntity?.billTo ||
        undefined,
      newStage: undefined,

      // computed
      selectedDamageIds:
        ArrayHelper.undefinedIfEmpty(
          (damageCostEvaluation || defaultValues?.sourceEntity)?.items
            ?.flatMap((x) => x.damages?.map((y) => y.damage?.id || "")?.filter(Boolean) ?? [])
            ?.filter(Boolean) ?? [],
        ) ||
        ArrayHelper.undefinedIfEmpty(defaultValues?.vehicleDamageIds) ||
        undefined,

      submit: "",
    }),
    [
      defaultVehicleDamages,
      damageCostEvaluation,
      defaultValues,
      userAffiliation,
      currentCurrency,
      vehicleReference,
    ],
  );

  const formikProps = useFormik<DamageCostEvaluationValues>({
    initialValues,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      // validation schema
    }),
    onSubmit: async (values, { setSubmitting, setStatus, setFieldError }) => {
      try {
        let values2: typeof values = {
          ...omit(values, ["submit"]),
          inspectedAt: values.inspectedAt ? moment(values.inspectedAt).utc().format() : undefined,
          attachments: values.attachments,
          items: values.items?.map((item) => {
            const repairSpecData = item.repairSpec
              ? {
                  ...Mapper.repairSpec.mapToDataInputDtoForBe(item.repairSpec),
                  id: item.repairSpec.id || undefined,
                }
              : undefined;

            return {
              ...item,
              damages: item.damages?.map((itemDamage) => {
                const damageData = itemDamage.damage
                  ? Mapper.vehicleDamage.mapToDataInputDtoForBe(itemDamage.damage)
                  : undefined;

                return {
                  ...itemDamage,
                  damage: damageData
                    ? {
                        ...damageData,
                        vehicleId: damageData?.vehicleId || values.vehicleId || values.vehicle?.id,
                        inspectedAt: damageData?.inspectedAt || moment().utc().format(),
                      }
                    : undefined,
                  damageLocal: undefined,
                };
              }),
              repairSpec: repairSpecData
                ? {
                    ...repairSpecData,
                    id: repairSpecData.id || undefined,
                    items: repairSpecData.items?.map((rsItem) => ({
                      ...rsItem,
                      id: rsItem.id || undefined,
                      custom: rsItem.custom
                        ? { ...rsItem.custom, name: rsItem.custom.name || undefined }
                        : undefined,
                    })),
                  }
                : undefined,
            };
          }),
          billTo: prepareGeneralBillToInfoInputDtoForApi(values.billTo),
        };
        values2 = IdHelper.setAllTempFeIdsToNullDeep(values2);

        if (formInfo.isCreate) {
          const response = await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsPost({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            damageCostEvaluationCreateDto: {
              ...values2,
            },
          });
          enqueueSnackbar("Created!", { variant: "success" });
          onCreate?.(response.data);
          onSave?.(response.data);
        } else {
          const response =
            await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdPut(
              {
                nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                damageCostEvaluationId: damageCostEvaluationId || "",
                damageCostEvaluationUpdateDto: {
                  ...values2,
                },
              },
            );
          enqueueSnackbar("Updated!", { variant: "success" });
          onUpdate?.(response.data);
          onSave?.(response.data);
        }

        if (mounted.current) {
          setSubmitting(false);
          setStatus({ success: true });
        }
      } catch (err) {
        if (mounted.current) {
          ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
          setSubmitting(false);
          setStatus({ success: false });
        }
      }
    },
  });

  const {
    values,
    setFieldValue,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    isSubmitting,
    submitForm,
  } = formikProps;

  return (
    <FormikProvider value={formikProps}>
      <BaseEntityCreateUpdate
        entityType={EntityType.DamageCostEvaluation}
        entityId={damageCostEvaluationId}
        entity={damageCostEvaluation}
        entityRequest={damageCostEvaluationRequest}
        isIniting={
          damageCostEvaluationRequest.isLoading ||
          defaultVehicleDamagesRequest.isLoading ||
          vehicleRequest.isLoading ||
          !tenantProfile.profile ||
          !profile ||
          userAffiliation.isIniting
        }
        extrasProps={{
          defaultImperativeHandle: {
            formWrapperRef: ref,
            formikProps: formikProps,
          },
          guardProps: {
            formikProps: formikProps,
          },
        }}
      >
        <FormContentBlock component={"form"} gap={1} noValidate onSubmit={handleSubmit}>
          <DevRenderCount isPositionFixed description='Form root' />

          <InferEntityAffiliationFromVehicleFormikComputedField
            formInfo={formInfo}
            values={{
              vehicleId: values.vehicleId,
              vehicle: values.vehicle,
              departmentIds: values.departmentIds,
              locationIds: values.locationIds,
            }}
            onComputed={(computed) => {
              setFieldValue("vehicle", computed.vehicle);
              setFieldValue("departmentIds", computed.departmentIds);
              setFieldValue("locationIds", computed.locationIds);
            }}
          />

          {/* Computed fields (read-only) */}
          <FormikComputedField<
            typeof values,
            Pick<
              typeof values,
              "items" | "subTotal" | "subTotalIncDiscount" | "discount" | "tax" | "total"
            >
          >
            debouncedDeps={{
              deps: [
                {
                  items: values.items?.map((x) => x.repairSpec),
                },
              ],
              wait: 1000,
            }}
            compute={(params) => {
              const computedItems = (params.values.items || []).map((item) => ({
                ...item,
                subTotal: item.repairSpec?.subTotal,
                subTotalIncDiscount: item.repairSpec?.subTotalIncDiscount,
                discount: item.repairSpec?.discount,
                tax: item.repairSpec?.tax,
                total: item.repairSpec?.total,
              }));
              const computedSummary = PriceHelper.calcLineItemsSummary(computedItems);

              return {
                items: computedItems,
                subTotal: computedSummary.subTotal,
                subTotalIncDiscount: computedSummary.subTotalIncDiscount,
                discount: computedSummary.discount,
                tax: computedSummary.tax,
                total: computedSummary.total,
              };
            }}
            onComputed={(newValue) => {
              // set only changed items to avoid re-renders
              newValue.items?.filter((newItem) => {
                const itemIndex = values.items?.findIndex((x) => x.tempId == newItem.tempId) ?? -1;
                const oldItem = values.items?.at(itemIndex);
                if (!oldItem) {
                  return false;
                }
                const isChanged =
                  newItem.subTotal !== oldItem.subTotal ||
                  newItem.subTotalIncDiscount !== oldItem.subTotalIncDiscount ||
                  newItem.discount !== oldItem.discount ||
                  newItem.tax !== oldItem.tax ||
                  newItem.total !== oldItem.total;
                if (isChanged) {
                  setFieldValue(`items[${itemIndex}]`, newItem);
                }
                return isChanged;
              });

              setFieldValue("subTotal", newValue.subTotal);
              setFieldValue("subTotalIncDiscount", newValue.subTotalIncDiscount);
              setFieldValue("discount", newValue.discount);
              setFieldValue("tax", newValue.tax);
              setFieldValue("total", newValue.total);
            }}
          />

          <FormikComputedField<typeof values, Pick<typeof values, "selectedDamageIds">>
            debouncedDeps={{
              deps: [
                {
                  // damageIds: flatten(values.items?.map((x) => x.damages?.map((y) => y.damageId))),
                  damageIds: values.items?.flatMap((x) =>
                    x.damages?.map((y) => y.damage?.id).filter(Boolean),
                  ),
                },
              ],
              wait: 300,
            }}
            compute={(params) => {
              const selectedDamageIds =
                params.values.items?.flatMap(
                  (x) => x.damages?.map((y) => y.damage?.id || "").filter(Boolean) ?? [],
                ) ?? [];
              return {
                selectedDamageIds,
              };
            }}
            onComputed={(newValue) => {
              setFieldValue("selectedDamageIds", newValue.selectedDamageIds);
            }}
          />

          <MemoWrapper
            props={{
              isCreate: formInfo.isCreate,
              canEdit: formPerms.canEdit,
              damageCostEvaluation,
              errors,
              originalId: values.originalId,
              tags: values.tags,
              vehicleId: values.vehicleId,
              departmentIds: values.departmentIds,
              locationIds: values.locationIds,
              contractId: values.contractId,
              customerId: values.customerId,
              damageDetectionId: values.damageDetectionId,
              externalNumber: values.externalNumber,
              currency: values.currency,
              inspectedAt: values.inspectedAt,
              isUseSupplierBranding: values.isUseSupplierBranding,
              billTo: values.billTo,
            }}
            arePropsEqualFactory={(params) =>
              MemoHelper.arePropsEqual.factory<typeof params.props>({
                isExcludeFunctionProps: true,
              })
            }
          >
            {() => (
              <>
                <DevRenderCount sx={{ mb: 1 }} description='Form first section' />

                {!formPerms.canEdit && (
                  <Alert severity='info' sx={{ my: 2 }}>
                    {`This cost evaluation is completed and can't be edited. You can edit only few a fields.`}
                  </Alert>
                )}

                {/* Tags */}
                <FormControl margin='dense' fullWidth>
                  <GeneralAttachedTagsInput
                    value={values.tags}
                    onChange={(newValue) => {
                      setFieldValue("tags", newValue);
                    }}
                  />
                  <FormHelperText error>
                    {ValidationHelper.getFormikErrorsAsString(errors?.tags, {
                      isIncludeNested: false,
                    })}
                  </FormHelperText>
                </FormControl>

                {!formInfo.isCreate && (
                  <TextField
                    fullWidth
                    disabled
                    label='Number'
                    margin='dense'
                    type='text'
                    value={damageCostEvaluation?.localNumber}
                    variant='outlined'
                  />
                )}

                {/* Vehicle */}
                <FormControl margin='dense' fullWidth error={Boolean(errors?.vehicleId)}>
                  <VehicleAutocompleteOrCreate
                    autocompleteProps={{
                      required: true,
                      disabled: !formPerms.canEditVehicle || !!defaultValues?.vehicle,
                      entityId: values.vehicleId,
                      isPreload: formInfo.isCreate,
                      onChange: (newValue) => {
                        setFieldValue(`vehicleId`, newValue?.id);
                        setFieldValue(`vehicle`, newValue);

                        if (values.vehicleId !== newValue?.id) {
                          setFieldValue(`contractId`, undefined);
                          setFieldValue(`contractLocal`, undefined);
                          setFieldValue(`damageDetectionId`, undefined);
                          setFieldValue(`initialVehicleDamageIds`, undefined);
                          setFieldValue(`items`, [createNewEmptyItem()]);
                          setFieldValue(
                            "departmentIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                          );
                          setFieldValue(
                            "locationIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.locationIds),
                          );
                        }
                      },
                    }}
                    createFormPlacement='modal'
                    onCreate={undefined}
                  />
                  <FormHelperText>{errors?.vehicleId}</FormHelperText>
                </FormControl>

                {/* Entity affiliation */}
                <EntityAffiliationInputV2
                  disabled={!formPerms.canEdit}
                  departments={{
                    departmentIds: values.departmentIds,
                    onChange: (departmentIds) => {
                      setFieldValue("departmentIds", departmentIds);
                    },
                    error: errors?.departmentIds,
                  }}
                  locations={{
                    locationIds: values.locationIds,
                    onChange: (locationIds) => {
                      setFieldValue("locationIds", locationIds);
                    },
                    error: errors?.locationIds,
                  }}
                />

                <TextField
                  fullWidth
                  error={Boolean(touched.externalNumber && errors.externalNumber)}
                  helperText={touched.externalNumber && errors.externalNumber}
                  label='External number'
                  margin='dense'
                  name='externalNumber'
                  onChange={(newValue) => {
                    setFieldValue("externalNumber", newValue.target.value);
                  }}
                  type='text'
                  value={values?.externalNumber || ""}
                  variant='outlined'
                />

                {/* Original DCE */}
                {values.originalId && (
                  <FormControl margin='dense' fullWidth error={Boolean(errors?.originalId)}>
                    <DamageCostEvaluationAutocomplete
                      entityId={values.originalId}
                      entity={undefined}
                      disabled
                      label='Original cost evaluation'
                    />
                    <FormHelperText error>{errors?.originalId}</FormHelperText>
                  </FormControl>
                )}

                {/* Source DD */}
                {values.damageDetectionId && (
                  <FormControl margin='dense' fullWidth error={Boolean(errors?.damageDetectionId)}>
                    <Stack direction='row' gap={1} sx={{ alignItems: "center" }}>
                      <Box sx={{ flex: 1 }}>
                        <DamageDetectionAutocomplete
                          entityId={values.damageDetectionId}
                          entity={undefined}
                          disabled
                          label='Source damage detection'
                        />
                      </Box>

                      <AppIconButton
                        onClick={() => {
                          setFieldValue("damageDetectionId", undefined);
                        }}
                      >
                        <AppIcon of='remove' />
                      </AppIconButton>
                    </Stack>
                    <FormHelperText error>{errors?.damageDetectionId}</FormHelperText>
                  </FormControl>
                )}

                {/* Contract */}
                {values.vehicleId && values.contractId && (
                  <FormControl margin='dense' fullWidth error={Boolean(errors?.contractId)}>
                    <Stack direction='row' gap={1} sx={{ alignItems: "center" }}>
                      <Box sx={{ flex: 1 }}>
                        <ContractAutocompleteOrCreate
                          autocompleteProps={{
                            disabled: true,
                            entityId: values.contractId,
                            isPreload: formInfo.isCreate,
                            searchFilters: {
                              vehicleId: values.vehicleId || undefined,
                              excludeStage: ContractStage.Draft,
                            },
                            onChange: (newValue) => {
                              setFieldValue(`contractId`, newValue?.id);
                              setFieldValue(`contractLocal`, newValue || undefined);
                            },
                          }}
                          createFormPlacement='modal'
                          onCreate={(newValue) => {
                            setFieldValue(`contractId`, newValue?.id);
                            setFieldValue(`contractLocal`, newValue || undefined);
                          }}
                        />
                      </Box>

                      <AppIconButton
                        onClick={() => {
                          setFieldValue("contractId", undefined);
                          setFieldValue("contractLocal", undefined);
                        }}
                      >
                        <AppIcon of='remove' />
                      </AppIconButton>
                    </Stack>
                    <FormHelperText error>{errors?.contractId}</FormHelperText>
                  </FormControl>
                )}

                {/* Customer */}
                <FormControl margin='dense' fullWidth>
                  <CustomerAutocompleteOrCreate
                    autocompleteProps={{
                      required: false,
                      isPreload: false,
                      entityId: values.customerId,
                      onChange: (newValue) => setFieldValue("customerId", newValue?.id),
                      textFieldProps: {
                        error: Boolean(errors?.customerId),
                        helperText: errors?.customerId,
                      },
                    }}
                    onCreate={(newValue) => setFieldValue("customerId", newValue?.id)}
                  />
                </FormControl>

                {values.vehicleId && (
                  <Box>
                    <FormControl
                      margin='dense'
                      fullWidth
                      required
                      error={Boolean(errors?.currency)}
                    >
                      <CurrencyAutocomplete
                        required
                        disabled={!formPerms.canEdit}
                        entity={values.currency}
                        onChange={(newValue) => setFieldValue("currency", newValue)}
                        textFieldProps={{
                          label: "Currency",
                          error: Boolean(errors?.currency),
                          helperText: ValidationHelper.getFormikErrorsAsString(errors?.currency),
                        }}
                      />
                      {/* <FormHelperText>Using currency from tenant settings</FormHelperText> */}
                    </FormControl>

                    <FormControl
                      margin='dense'
                      fullWidth
                      error={Boolean(touched.inspectedAt && errors?.inspectedAt)}
                    >
                      <AppDateTimePicker
                        disabled={!formPerms.canEdit}
                        label='Inspected at'
                        value={(values.inspectedAt && moment(values.inspectedAt)) || null}
                        format={DATETIME_FORMATS.DISPLAY_DATETIME}
                        onChange={(newValue: Moment | null) => {
                          setFieldValue("inspectedAt", newValue?.format() || null);
                        }}
                      />
                      <FormHelperText>{touched.inspectedAt && errors?.inspectedAt}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin='dense'>
                      <GeneralBillToInfoInput
                        formikProps={FormikHelper.getSubProps(
                          formikProps,
                          "billTo",
                          (x) => x.billTo,
                        )}
                        // don't auto-infer for DCE, only set manually or via default values from T2T request
                        inferFromEntities={[{}]}
                      />
                    </FormControl>

                    {/* Include supplier branding */}
                    <FormControl margin='dense' fullWidth>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isUseSupplierBranding}
                            disabled={!formPerms.canEdit}
                            onChange={(e) =>
                              setFieldValue(`isUseSupplierBranding`, e.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography component='span' sx={{ display: "flex" }}>
                            Include supplier branding
                          </Typography>
                        }
                      />
                    </FormControl>
                  </Box>
                )}
              </>
            )}
          </MemoWrapper>

          {values.vehicleId && values.vehicle && (
            <>
              {/* Vehicle actual state */}
              <FoldableBlock
                defaultIsFolded={true}
                trigger={{
                  label: <Typography variant='h2'>Vehicle actual state</Typography>,
                }}
              >
                <VehicleActualStateView
                  vehicleId={values.vehicleId}
                  displayProps={{
                    vehicle: false,
                    breadcrumbs: false,
                    header: false,
                    actions: false,
                    viewVariant: ViewVariant.Tab,
                  }}
                />
              </FoldableBlock>

              {/* Items */}
              <FoldableBlock
                defaultIsFolded={false}
                trigger={{
                  label: <Typography variant='h2'>Items *</Typography>,
                }}
              >
                <DevRenderCount sx={{ mb: 2 }} description='Items section' />

                <DamageCostEvaluationItemsInput
                  formInfo={formInfo}
                  formPerms={formPerms}
                  formSettings={formSettings}
                  formikProps={formikProps}
                  defaultVehicleDamages={defaultVehicleDamages}
                />
              </FoldableBlock>

              <MemoWrapper
                props={{
                  errors,
                  notes: values.notes,
                  contractId: values.contractId,
                  initialAttachments: values.initialAttachments,
                  attachments: values.attachments,
                  inspector: values.inspector,
                  isSendToCustomer: values.isSendToCustomer,
                }}
                arePropsEqualFactory={(params) =>
                  MemoHelper.arePropsEqual.factory<typeof params.props>({
                    isExcludeFunctionProps: true,
                  })
                }
              >
                {() => (
                  <Stack spacing={1}>
                    <DevRenderCount description='Form last section' />

                    {/* Other */}
                    <FoldableBlock
                      defaultIsFolded={false}
                      trigger={{
                        label: <Typography variant='h2'>Other</Typography>,
                      }}
                    >
                      <AppTextArea
                        error={Boolean(touched.notes && errors?.notes)}
                        fullWidth
                        helperText={touched.notes && errors?.notes}
                        mode='notes'
                        margin='dense'
                        name='notes'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.notes}
                        variant='outlined'
                      />

                      <FormControl fullWidth component='fieldset' margin='dense'>
                        <FileUploader
                          multiple
                          defaultFiles={FileItem.createManyFrom(values.initialAttachments)}
                          onChange={(newFiles) => {
                            setFieldValue(
                              `attachments`,
                              FileItem.toManyGeneralAttachmentInputDto(newFiles),
                            );
                          }}
                          onUploadStarted={() => {
                            setFieldValue("isAttachmentsUploading", true);
                          }}
                          onUploadFinished={() => {
                            setFieldValue("isAttachmentsUploading", false);
                          }}
                          onValidationStatusChange={(filesValidationStatus) => {
                            if (filesValidationStatus)
                              setFieldValue(
                                "isAttachmentsHaveErrors",
                                Object.values(filesValidationStatus).some((x) => x === false),
                              );
                          }}
                        />
                      </FormControl>
                    </FoldableBlock>

                    <FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            disabled
                            checked={values.inspector?.isCurrentUser}
                            onChange={handleChange}
                            name='inspector.isCurrentUser'
                          />
                        }
                        label={
                          <AppTypography
                            decoration={{ variant: "helpText" }}
                            tooltipProps={{
                              title: "Currently logged in user evaluated the damages",
                            }}
                          >
                            Inspected by{" "}
                            {values.inspector?.personName
                              ? `${values.inspector.personName?.firstName} ${values.inspector.personName?.lastName}`
                              : profile?.personName?.name}
                          </AppTypography>
                        }
                      />
                    </FormControl>

                    {values.contractId && (
                      <FormControl margin='dense' fullWidth>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.isSendToCustomer ?? undefined}
                              onChange={(e) => setFieldValue("isSendToCustomer", e.target.checked)}
                            />
                          }
                          label='Send to customer'
                        />
                      </FormControl>
                    )}
                  </Stack>
                )}
              </MemoWrapper>

              <GeneralValidationError errors={errors} />

              <FormActions>
                <AppSplitDropdownButton
                  isSelectAndTriggerAction={false}
                  buttonProps={{
                    variant: "contained",
                    color: "primary",
                    type: "submit",
                    fullWidth: true,
                    loading: isSubmitting,
                    disabled:
                      !formPerms.canEdit ||
                      values.isAttachmentsUploading ||
                      values.isAttachmentsHaveErrors ||
                      values.items?.some((x) => x.isAttachmentsHaveErrors === true),
                  }}
                  options={[
                    {
                      content: "Save",
                      active:
                        !displayProps?.submitActionType ||
                        displayProps?.submitActionType === "save",
                      onClick: () => {
                        submitForm();
                      },
                    },
                    {
                      content: "Save & Complete",
                      if:
                        formInfo.isCreate ||
                        (formInfo.isUpdate &&
                          damageCostEvaluation?.stageHistory?.stage !==
                            DamageCostEvaluationStage.Completed),
                      active: displayProps?.submitActionType === "saveAndComplete",
                      onClick: () => {
                        const newValue: GeneralStageUpdateDtoOfDamageCostEvaluationStage = {
                          newStage: DamageCostEvaluationStage.Completed,
                        };
                        setFieldValue("newStage", newValue);
                        submitForm();
                      },
                    },
                  ]}
                />

                {onCancel && (
                  <Button
                    variant='outlined'
                    color='text'
                    disabled={isSubmitting}
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                )}
              </FormActions>
            </>
          )}
        </FormContentBlock>
      </BaseEntityCreateUpdate>
    </FormikProvider>
  );
});
